import { Button } from '@mui/material';
import { styled } from '@mui/styles';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux-latest';
import { AppDispatch, useAppSelector } from '../../../redux/store/configureStore';
import { theme } from '../../../theme';
import { validateUrl } from '../../auth/validate';
import { FormInput } from '../../../components/atoms/input';
import { refreshCurrentUserData, updateOrganization } from '../../../redux/actions';
import { UpdateUserOrganizationDto } from '@zaplify/users/shared';
import { useMsal } from '@azure/msal-react';

const colors = (theme.palette as any).colors;

const UsersViewStyled = styled('div')(({ theme }) => ({
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'start',

    '& .MuiTableContainer-root': {
        border: `1px solid ${colors.greyInformationLight}`,
        borderRadius: 8,
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0px 2px 6px rgba(112, 144, 176, 0.07);',
    },
    '& .MuiTableHead-root': {
        maxHeight: 43,
        padding: 0,
        backgroundColor: colors.whiteDark,
        fontWeight: 500,
        fontSize: 13,
        color: colors.black,
        lineHeight: '19px',

        '& .MuiTableRow-root': {
            '& .MuiTableCell-root': {
                paddingTop: 10,
                paddingBottom: 10,
            },
            '& th:first-of-type': {
                paddingLeft: 29,
            },
            '& th:last-of-type': {
                paddingRight: 29,
            },
        },
    },
    '& .MuiTableBody-root': {
        '& .MuiTableRow-root': {
            '& td:first-of-type': {
                paddingLeft: 29,
                paddingRight: 29,
            },
        },
    },
    '& .MuiInputBase-input': {
        padding: '10px',
        fontSize: '0.8rem',
    },
}));
const DetailsStyled = styled('div')(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 10,
    marginTop: 16,
    padding: 20,
    background: '#FFFFFF',
    border: '1px solid #E4E6EB',
    boxShadow: '0px 2px 6px rgb(112 144 176 / 7%)',
    borderRadius: 14,
    '& .billing-details-container': {
        display: 'flex',
        gap: 15,
        width: '100%',
        '& > .billing-details-box': {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            background: '#FFFFFF',
            border: '1px solid #E4E6EB',
            borderRadius: 4,
            padding: 10,
            '& > .details-container': {
                display: 'flex',
                flexDirection: 'column',
                '& .value': {
                    fontWeight: 500,
                    marginBottom: 3,
                    '& svg': {
                        marginBottom: '-6px',
                    },
                },
                '& .grey-value': {
                    color: '#6B7985',
                    fontSize: 14,
                },
            },
            '& > button': {
                '& > span:first-child': {
                    textDecoration: 'underline',
                    color: theme.palette.primary.light,
                },
            },
        },
    },
    '& > button': {
        '& > span:first-child': {
            textDecoration: 'underline',
            color: theme.palette.primary.light,
        },
    },
}));
const OrganizationSetting = () => {
    const dispatch = useDispatch<AppDispatch>();
    const initialErrors = {
        website: '',
    };
    const { name, domain, website } = useAppSelector((state) => state.user.zaplifyUser.userOrganization);
    const [errors, setErrors] = useState(initialErrors);
    const [organizationWebsite, setOrganizationWebsite] = useState<string>(website);
    const msal = useMsal();

    const shouldDisable = !name || !domain || !organizationWebsite || errors.website.length > 0;

    const handleSaveSettings = () => {
        const organizationDTO: UpdateUserOrganizationDto = {
            name,
            domain,
            website: organizationWebsite,
        };
        dispatch(updateOrganization(msal.instance, organizationDTO));
        dispatch(refreshCurrentUserData(msal.instance));
    };

    const onOrganizationWebsiteChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        setErrors({
            ...errors,
            website: validateUrl(event.target.value),
        });
        setOrganizationWebsite(event.target.value);
    };

    return (
        <>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <FormInput
                    disabled={true}
                    label="Organization Name"
                    name={'name'}
                    value={name}
                    placeholder="Enter your organization name"
                />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <FormInput
                    disabled={true}
                    label="Organization Domain"
                    name={'domain'}
                    value={domain}
                    placeholder="Enter your domain"
                />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                <FormInput
                    label="Organization Website URL"
                    name={'website'}
                    value={organizationWebsite}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => onOrganizationWebsiteChange(event)}
                    placeholder="Enter your website URL"
                    errorMessage={errors.website}
                    onBlur={(event) =>
                        setErrors({
                            ...errors,
                            website: validateUrl(event.target.value),
                        })
                    }
                />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '20px', width: '100%' }}>
                <Button variant="contained" color="primary" onClick={handleSaveSettings} disabled={shouldDisable}>
                    Save
                </Button>
            </div>
        </>
    );
};

const OrganizationSettingView = () => {
    return (
        <UsersViewStyled>
            <div
                style={{
                    width: '100%',
                }}
            >
                <div
                    style={{
                        maxHeight: 48,
                        marginBottom: 29,
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: 15,
                        fontWeight: 500,
                    }}
                >
                    <div
                        style={{
                            marginBottom: 24,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 5,
                        }}
                    >
                        <div style={{ color: colors.black, fontSize: 24, fontWeight: 500 }}>Organization Settings</div>
                    </div>
                </div>
                <DetailsStyled>
                    <OrganizationSetting />
                </DetailsStyled>
            </div>
        </UsersViewStyled>
    );
};

export default OrganizationSettingView;
